import * as React from "react";

import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DefaultFormModelMap, FormModelMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { LayoutGridProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/layout-grid";

const DefaultRow = DefaultFormModelMap.Row.component;

// Field labels render with a widget-specific `data-role` (e.g. "textline-label", "textarea-label").
// Decorative labels (e.g. a checkbox's `aria-hidden` click-target label) are excluded since they
// don't carry visible label text and would otherwise skew the height measurement below.
const LABEL_SELECTOR = '[data-role$="-label"]:not([aria-hidden="true"])';

// Checkboxes (Kontrollkästchen) render their label next to the input instead of above it, so there
// is no separate label row whose height keeps the input aligned with other fields in the row.
const CHECKBOX_LABEL_SELECTOR = '[data-role="checkbox-label"]';
const CHECKBOX_CONTROL_SELECTOR = '[data-role="checkbox-control"]';

/**
 * Equalizes the height of all field labels within a row (via top padding) so that all inputs of a
 * row start at the same vertical position, regardless of how many lines their individual labels
 * wrap to. Bottom-aligning the row/grid instead would achieve the same for the initial render, but
 * breaks auto-expanding fields (they'd grow upwards instead of downwards).
 *
 * Checkboxes don't have a label row to pad, so instead the whole checkbox control is shifted down
 * by the row's max label height, keeping it visually on the same level as the other row fields.
 */
export function EqualizeRowLabelHeights(
	props: FormModelMap.FormModelComponentProps<FormModel.Row> & { readonly layout: LayoutGridProps.ResponsiveConfig }
): React.ReactNode {
	const containerRef = React.useRef<HTMLDivElement>(null);

	const equalizeLabelHeights = React.useCallback(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const labels = Array.from(container.querySelectorAll<HTMLElement>(LABEL_SELECTOR));
		if (labels.length < 2) {
			return;
		}

		const checkboxControls = Array.from(container.querySelectorAll<HTMLElement>(CHECKBOX_CONTROL_SELECTOR));

		// Reset before measuring, so previously applied offsets don't skew the new measurement.
		labels.forEach(label => {
			label.style.paddingTop = "";
		});
		checkboxControls.forEach(control => {
			control.style.marginTop = "";
		});

		const alignableLabels = labels.filter(label => !label.matches(CHECKBOX_LABEL_SELECTOR));
		const naturalHeights = alignableLabels.map(label => label.getBoundingClientRect().height);
		const maxLabelHeight = naturalHeights.length > 0 ? Math.max(...naturalHeights) : 0;

		alignableLabels.forEach((label, index) => {
			const diff = maxLabelHeight - naturalHeights[index];
			label.style.paddingTop = diff > 0 ? `${diff}px` : "";
		});

		if (maxLabelHeight > 0) {
			checkboxControls.forEach(control => {
				control.style.marginTop = `${maxLabelHeight}px`;
			});
		}
	}, []);

	React.useLayoutEffect(() => {
		equalizeLabelHeights();

		const container = containerRef.current;
		if (!container || typeof ResizeObserver === "undefined") {
			return;
		}

		// Re-run whenever a cell resizes (e.g. due to responsive column reflow).
		const observer = new ResizeObserver(() => equalizeLabelHeights());
		Array.from(container.children).forEach(child => observer.observe(child));

		return () => observer.disconnect();
	});

	return (
		<div ref={containerRef} style={{ display: "contents" }}>
			<DefaultRow {...props} />
		</div>
	);
}
